/**
 * @typedef {import('@wkda/funnel-modules').ModulesLogger} ModulesLogger
 * */

/**
 * Creates a client-side logger that delegates to RUM (Real User Monitoring).
 *
 * NOTE: As of this migration, client-side logging exclusively uses RUM/APM.
 * The legacy POST-based logger has been removed to simplify infrastructure.
 *
 * @param {import("@wkda/internal-types").IBaseAppConfig} config
 * @param {import("@wkda/elastic-rum").RumLogger} rumLogger - RUM logger (required)
 * @return {import("@wkda/funnel-modules").ModulesLogger}
 * */
export function createClientLogger(config, rumLogger) {
  // RUM logger is required for client-side logging
  if (!rumLogger) {
    console.error('[createClientLogger] FATAL: RUM logger is required for client-side logging. ' + 'Logs will only be sent to console in development mode.');

    // Return no-op logger that only logs to console in development
    return createNoOpLogger(config);
  }

  /**@satisfies {ModulesLogger} */
  return {
    info({
      message,
      meta
    }) {
      //@ts-expect-error check meta
      rumLogger.log(meta?.name, meta?.scope, message);
    },
    error({
      error
    }) {
      if (error instanceof Error) {
        rumLogger.error(error);
      }
    },
    log(logRecord) {
      rumLogger.log(logRecord.meta?.name, logRecord.meta?.scope, logRecord.message);
    }
  };
}

/**
 * Creates a no-op logger that only logs to console in local development.
 * Used as fallback when RUM is not available.
 *
 * @param {import("@wkda/internal-types").IBaseAppConfig} config
 * @return {import("@wkda/funnel-modules").ModulesLogger}
 */
function createNoOpLogger(config) {
  const isDev = config.env === 'local';
  return {
    info({
      message,
      meta
    }) {
      if (isDev) {
        console.info('[CLIENT-LOGGER:INFO]', message, meta);
      }
    },
    error({
      error,
      message,
      meta
    }) {
      if (isDev) {
        console.error('[CLIENT-LOGGER:ERROR]', message || error, meta);
      }
    },
    log(logRecord) {
      if (isDev) {
        console.log('[CLIENT-LOGGER:LOG]', logRecord);
      }
    }
  };
}